import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MmcSettingsAssetClassGridLayoutService} from './mmc-settings-asset-class.grid-layout.service';
import {CalltoactionService} from "@irb-common/components/calltoaction/calltoaction.service";
import {AbstractControl, FormControl} from "@angular/forms";
import {TooltipService} from "@irb-common/components/tooltip/tooltip.service";
import {IrbMMCSettingsService} from "@irb/irb-settings/settings-mmc/irb-mmc-settings.service";
import {AppService} from "@irb/app.service";
import {IrbAppEvents} from "@irb/app.events";

@Component({ standalone: false,
    selector: 'irb-mmc-asset-class-settings',
    templateUrl: './mmc-asset-class-settings.component.html',
    styleUrls: ['./mmc-asset-class-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MmcAssetClassSettingsComponent implements OnInit {
    gridOptions: any;
    grid: any;
    frameworkComponents: any;
    _data: any;
    _originalData: any;
    assetClassName: any;
    inputErrorMessage: any;
    @ViewChild('assetClassValidationTextBox') assetClassValidationTextBox: ElementRef;
    assetClassValidationTextBoxInputControl: FormControl = new FormControl();
    @Input()
    set originalData(value:any) {
        this._originalData = value;
    }
    @Input()
    get data() {
        return this._data;
    }

    @Output()
    changed: EventEmitter<any> = new EventEmitter<any>();


    set data(value: any) {
        this._data = value;
        this.assetClassName = null;
        this.loadGrid();
    }

    constructor(private gls: MmcSettingsAssetClassGridLayoutService,
                private ctas: CalltoactionService,
                private tts: TooltipService,
                private mmcSettingsService: IrbMMCSettingsService,
                private as: AppService) {
    }

    ngOnInit(): void {
        this.initializeFormStructure();
        this.gridOptions = {
            columnDefs: this.gls.getColumnDefs(this),            
            floatingFilter: false,
            overlayNoRowsTemplate:
                "<span class='ag-overlay-loadGriding-center ag-overlay-message'>No asset classes defined.</span>",
            context: {componentParent: this},
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
        };
        
        this.frameworkComponents = this.gls.getFrameworkComponents();
    }

    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        const lbl = node.data.label || '';
        return !node.data.isUIdeleted && lbl.toLowerCase() !== 'none';
    }

    delete(data: any) {
        this.assetClassName = null;
        if (data.isNew) {
            let itemToRemoveIndex = this._data.findIndex((x: any) => x.label === data.label);
            let removedItem = this.data.splice(itemToRemoveIndex, 1);
            this.loadGrid();
            this.triggerChangedEvent();
        } else {
            this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
            this.mmcSettingsService.validateMMCSetting(data.id, 'ASSET_CLASS').subscribe((response: any) => {
                if (!response.inUse) {
                    data.isUIdeleted = true;
                    this.loadGrid();
                    this.triggerChangedEvent();
                } else {
                    this.triggerAlreadyInUse(data);
                }
            }, () => {

            }, () => {
                this.cancelDelete();
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            });

        }
        // console.log(data);
    }

    cancelDelete() {
        this.ctas.remove();
    }

    onGridReady(params: any) {
        this.grid = params;
    }

    getCount() {
        return this._data?.length ? this._data.length : 0;
    }
    getNonDeletedCount() {
        if(this._data) {
            let nonDeleted =  this._data.filter((x:any) => !x.isUIdeleted);
            return nonDeleted.length;
        } else  {
            return 0;
        }

    }
    onAddAssetClass() {
        if (this.assetClassValidationTextBoxInputControl.valid) {
            let hasValue = this._data.findIndex((x: any) => (x.label === this.assetClassName));
            if(hasValue >= 0) {
                this._data[hasValue].isUIdeleted = false;
            } else {
                let newObj: any = {
                    id: null,
                    description: "",
                    label: this.assetClassName,
                    isNew: true,
                    isUIdeleted: false,
                    isUpdated: false,
                    settingType: "ASSET_CLASS",
                    envId: null,
                    inUse: false,
                    versionId: null
                };
                this.data.push(newObj);
            }

            this.assetClassName = null;
            this.loadGrid();
            this.triggerChangedEvent();
        }
    }

    initializeFormStructure() {
        this.assetClassValidationTextBoxInputControl = new FormControl(this.assetClassName,
            [this.uniqueLabelValidatorFn.bind(this)]);
    }

    uniqueLabelValidatorFn(control: AbstractControl): { [key: string]: any } | null {
        if (this._data) {
            if (control.value) {
                let hasValue = this._data.findIndex((x: any) => (x.label === control.value && !x.isUIdeleted));
                if (hasValue >= 0) {
                    this.inputErrorMessage = "Asset class \"" + control.value + "\" is already present.";
                    return {'hasUniqueLabel': true};
                } else {
                    this.inputErrorMessage = null;
                    this.tts.remove();
                    return null;

                }
            } else {
                this.inputErrorMessage = null;
                this.tts.remove();
                return null;
            }
        } else {
            this.inputErrorMessage = null;
            this.tts.remove();
            return null;
        }
    }

    onCellValueChanged(params: any) {
        const colId = params.column.getId();
        if (colId === "label") {
            if(!params.data.isNew) {
                let originalObj = this._originalData.find((x:any)=> x.id === params.data.id);
                if(originalObj.label !== params.data.label){
                    params.data.isUpdated = true;
                } else {
                    params.data.isUpdated = false;
                }
            }
            this.triggerChangedEvent();
        }
    }

    private loadGrid() {
        if (this.grid && this.grid.api && this.data) {
            this.grid.api.setGridOption('rowData', this.data);
        }
    }

    triggerChangedEvent() {
        this.changed.next(this.data);
    }

    triggerAlreadyInUse(data: any) {
        let message = "The asset class \"" + data.label + "\" cannot be deleted because it is currently in use.";
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            severity: 'error',
            summary: '',
            detail: message
        });
    }
}
